/**
 * Platform admin card: start a GE2 (Gemini Embedding 2) backfill and poll its
 * status. One component for both backfills; the config names the endpoint
 * and copy.
 */
import React, { useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { apiRequest } from '../../services/apiClient';
import { showDialog } from '../../utils/dialogs';

export interface Ge2BackfillConfig {
  /** `/start` and `/status` are appended. */
  endpointBase: string;
  title: string;
  description: string;
  /** Used in the started/failed dialogs, e.g. "GE2 backfill". */
  jobLabel: string;
  /** What gets embedded, e.g. "rows" or "memories". */
  progressNoun: string;
}

// EMBD-05
export const GE2_DOCUMENT_CHUNKS_BACKFILL: Ge2BackfillConfig = {
  endpointBase: '/admin/backfill/ge2',
  title: 'GE2 Embedding Backfill',
  description:
    'Populate the embedding_ge2 column on all document_chunks using Google Gemini Embedding 2. GE2 is the active retrieval embedding provider.',
  jobLabel: 'GE2 backfill',
  progressNoun: 'rows',
};

// EMIG-03
export const GE2_SEMANTIC_MEMORIES_BACKFILL: Ge2BackfillConfig = {
  endpointBase: '/admin/backfill/ge2-memories',
  title: 'GE2 Semantic Memories Backfill',
  description:
    'Populate the embedding_ge2 column on all semantic_memories rows using Google Gemini Embedding 2. Run AFTER document_chunks backfill is complete.',
  jobLabel: 'GE2 memories backfill',
  progressNoun: 'memories',
};

interface BackfillStatus {
  status: string;
  total: number;
  done: number;
  failed_count: number;
}

export default function Ge2BackfillCard({ config }: { config: Ge2BackfillConfig }) {
  const [startLoading, setStartLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [result, setResult] = useState<BackfillStatus | null>(null);

  const handleCheckStatus = async () => {
    try {
      setStatusLoading(true);
      const status = await apiRequest<BackfillStatus>({
        endpoint: `${config.endpointBase}/status`,
        method: 'GET',
      });
      setResult(status);
    } catch (error: any) {
      const message = error?.message || `Failed to fetch ${config.jobLabel} status`;
      showDialog(message);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      setStartLoading(true);
      const started = await apiRequest<{ task_id: string; status: string }>({
        endpoint: `${config.endpointBase}/start`,
        method: 'POST',
      });
      showDialog(`${config.jobLabel} started. Task ID: ${started.task_id}`);
      // Immediately fetch status
      await handleCheckStatus();
    } catch (error: any) {
      const message = error?.message || `Failed to start ${config.jobLabel}`;
      showDialog(message);
    } finally {
      setStartLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-0">
        <Text className="text-lg font-semibold text-foreground">
          {config.title}
        </Text>
        <Text className="text-sm text-muted-foreground mt-1">
          {config.description}
        </Text>
      </CardHeader>
      <CardContent className="gap-4">
        <View className="flex-row gap-3">
          <Button
            variant="outline"
            disabled={startLoading || statusLoading || result?.status === 'running'}
            onPress={handleStart}
            className="flex-row items-center justify-center gap-2 flex-1"
          >
            {startLoading ? (
              <ActivityIndicator size="small" color="#1F2937" />
            ) : (
              <MaterialIcons name="play-arrow" size={18} color="#1F2937" />
            )}
            <Text className="text-sm font-semibold text-foreground">
              Start Backfill
            </Text>
          </Button>
          <Button
            variant="outline"
            disabled={statusLoading || startLoading}
            onPress={handleCheckStatus}
            className="flex-row items-center justify-center gap-2 flex-1"
          >
            {statusLoading ? (
              <ActivityIndicator size="small" color="#1F2937" />
            ) : (
              <MaterialIcons name="refresh" size={18} color="#1F2937" />
            )}
            <Text className="text-sm font-semibold text-foreground">
              Refresh Status
            </Text>
          </Button>
        </View>

        {result && (
          <View className="bg-gray-50 rounded-lg p-4 gap-2">
            <View className="flex-row justify-between">
              <Text className="text-sm font-medium text-foreground">
                Status: <Text className={
                  result.status === 'complete' ? 'text-green-600' :
                  result.status === 'running' ? 'text-blue-600' : 'text-muted-foreground'
                }>{result.status.toUpperCase()}</Text>
              </Text>
            </View>
            {result.total > 0 && (
              <View className="gap-1">
                <Text className="text-sm text-muted-foreground">
                  Progress: {result.done} / {result.total} {config.progressNoun} embedded
                </Text>
                {result.failed_count > 0 && (
                  <Text className="text-sm text-red-600">
                    Failed: {result.failed_count} rows (recorded in Redis for retry)
                  </Text>
                )}
                {result.status === 'complete' && result.done === result.total && (
                  <Text className="text-sm text-green-600 font-medium">
                    All {config.progressNoun} embedded.
                  </Text>
                )}
              </View>
            )}
          </View>
        )}
      </CardContent>
    </Card>
  );
}
